'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { debounce, type DebouncedFn } from '@funkey/shared/utils/js';
import { type DebouncedFunc, throttle } from 'lodash';

/** Takes in a callback and debounces its calls.
 *
 * @param [delay=500] defaults to `500`ms
 * @see [The original source code](https://usehooks-ts.com/react-hook/use-debounce-callback) for more information. */
export function useDebouncedCallback<T extends (...args: any[]) => ReturnType<T>>(
    func: T,
    delay = 500,
    dependencies: readonly unknown[] = [],
): DebouncedFn<T> {
    const fnRef = useRef<T>(func);
    fnRef.current = func;

    const isPending = useRef(false);

    const debounced = useMemo<DebouncedFn<T>>(() => {
        const debouncedFuncInstance: DebouncedFn<T> = debounce((...args: Parameters<T>) => {
            isPending.current = false;
            return fnRef.current(...args);
        }, delay);

        const wrappedFunc: DebouncedFn<T> = (...args: Parameters<T>) => {
            isPending.current = true;
            return debouncedFuncInstance(...args);
        };

        wrappedFunc.cancel = () => debouncedFuncInstance.cancel();
        wrappedFunc.isPending = () => isPending.current;
        wrappedFunc.flush = () => debouncedFuncInstance.flush();

        return wrappedFunc;
    }, [delay, ...dependencies]);

    useEffect(() => () => debounced.cancel(), []);

    return debounced;
}

export function useDebouncedValue<T>(value: T | (() => T), delay?: number) {
    const [state, setState] = useState(value);
    const debouncedSetState = useDebouncedCallback(setState, delay);

    useEffect(() => {
        debouncedSetState(value);
    }, [value]);

    return state;
}

export function useThrottledCallback<T extends (...args: any[]) => any>(
    callback: T,
    throttleMs: number,
): DebouncedFunc<T> {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const ref = useRef<T>(null!);
    ref.current = callback;

    const wrappedFunc = useMemo(
        () =>
            throttle<T>(((...args) => ref.current(...args)) as T, throttleMs, {
                leading: true,
                trailing: true,
            }),
        [throttleMs],
    );

    useEffect(() => () => wrappedFunc.cancel(), []);

    return wrappedFunc;
}

/** Returns an internal `state` copy of `factory` that's throttled by `throttleMs` amound of time.
 *
 * If no `dependencies` are passed, it will sync it's state every time the `factory` changes.
 * Esle it will only sync if the `dependencies` values have changed. */
export function useThrottledValue<T>(
    factory: T | (() => T),
    throttleMs: number,
    dependencies?: unknown[],
) {
    const [state, setState] = useState(factory);
    const initialRenderRef = useRef(true);
    const sync = useThrottledCallback(setState, throttleMs);

    useEffect(
        () => {
            if (initialRenderRef.current === true) {
                initialRenderRef.current = false;
                return;
            }

            sync(factory);
        },
        dependencies || [factory],
    );

    return state;
}
